import "@/styles/globals.css";
import "@/styles/skeleton.css";
import Layout from "@/src/components/common/loader";
import Provider from "@/src/provider";
import Head from "next/head";
import MetaTags from "@/src/components/seo/BaseSeo";
import {
    NEXT_FRONTEND_URL,
    NEXT_PUBLIC_LOGO,
    NEXT_PUBLIC_DOMAIN_URL,
    seoDefaults,
    NEXT_IMAGE_CDN,
} from "@/src/lib/util/config";
import { Snackbar } from "@/src/lib/ui";
import { useMemo, useState } from "react";
import ErrorBoundary from "@/src/lib/hoc/ErrorBoundary";
import PushNotifications from "@/src/components/common/pushNotification";

export default function App({ Component, pageProps }) {
    // Extract SEO data from pageProps
    const seoData = Array.isArray(pageProps?.data)
        ? pageProps.data[0]
        : pageProps?.data;

    // Resolve title and description
    let title = seoData?.seoTitle
        ? `${seoData?.seoTitle.replace(" | HOD", "").trim()} | HOD`
        : `${seoDefaults.name.replace(" | HOD", "").trim()} | HOD`;
    // Overwriting for Home Page
    title =
        title ===
        `HOD | House Of Diagnostics | Diagnostic Centre | Pathology Lab | HOD`
            ? `HOD | House Of Diagnostics | Diagnostic Centre | Pathology Lab`
            : title;
    const description = seoData?.seoDescription || seoDefaults.description;

    // Compute banner image
    const bannerImage = useMemo(() => {
        if (seoData?.bannerImage) {
            return `${NEXT_IMAGE_CDN}/${seoData?.bannerImage}`;
        } else if (seoData?.configProductCard?.categoryBannerImage) {
            return `${NEXT_IMAGE_CDN}/${seoData?.configProductCard.categoryBannerImage}`;
        }
        return NEXT_PUBLIC_LOGO;
    }, [seoData]);

    // Construct page URL
    const pageUrl = seoData?.routerPath
        ? `${NEXT_FRONTEND_URL}${seoData?.routerPath}`
        : NEXT_FRONTEND_URL;

    const getLayout = Component.getLayout ?? ((page) => page);

    // BLOGS DETAILS PAGE
    const structuredBlogDetailsData = JSON.stringify({
        "@context": seoDefaults.context,
        "@type": "BlogPosting",
        mainEntityOfPage: {
            "@type": "WebPage",
            "@id": `https://www.hod.care/blog/${seoData?.urlSlug}`,
        },
        // title: title,
        headline: title,
        description: description,
        image: bannerImage,
        author: {
            "@type": "Organization",
            name: seoData?.name,
            url: seoDefaults?.baseUrl,
        },
        publisher: seoDefaults.publisher,
        datePublished: "2023-02-04", // Tobe added in the API Response
    });

    // FACILITIES DETAILS PAGE
    const structuredFacilityDetailsData = JSON.stringify({
        "@context": seoDefaults.context,
        "@type": "Product",
        // title: title,
        name: seoData?.urlSlug,
        description: description,
        image: bannerImage,
        brand: seoDefaults?.brand,
    });

    // PRODUCT PAGE
    const productStructuredData = JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Product",
        name: seoData?.productName,
        image: bannerImage,
        // title: title,
        description: description,
        offers: {
            "@type": "Offer",
            url: pageUrl,
            priceCurrency: "INR",
            price: `${seoData?.maxPrice}`, // LABHAM: default rate needs to be set?? - Need to send default rates in response
            availability: "InStock",
        },
    });

    // CAREERS DETAILS PAGE
    const structuredCareersDetailsData = JSON.stringify({
        "@context": seoDefaults.context,
        "@type": "Job",
        name: seoData?.seoTitle,
        description: seoData?.seoDescription,
        brand: seoDefaults?.brand,
    });

    //DEFAULT SEO DATA
    const defaultStructuredData = JSON.stringify({
        "@context": "https://schema.org/",
        "@type": "Organization",
        // title: title,
        name: seoDefaults.name,
        // description: description || seoDefaults.description,
        url: seoDefaults.baseUrl,
        logo: seoDefaults.logo,
        contactPoint: seoDefaults.contactPoint,
        sameAs: [...seoDefaults.webUrls, "https://www.hod.care/"],
    });

    const defaultStructuredDataPages = [
        "",
        "pages/gallery",
        "pages/affordable-diagnostic-services",
        "pages/about-hod",
        "pages/corporate-enquiry",
        "pages/franchise",
        "pages/contact-us",
        "press",
        "offers",
        "locate-us",
        "facilities",
        "find-a-test",
        "specialization",
        "health-packages",
        "blog",
        "careers",
    ];

    const dataLayerScript = `
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "view_item",
          ecommerce: {
            currency: "INR",
            value: "${seoData?.maxPrice}",
            items: [
              {
                item_id: "${seoData?.productTypeId}",
                item_name: "${seoData?.productName}",
                item_brand: "HOD",
                price: ${seoData?.maxPrice},
                quantity: 1,
                item_category: "${seoData?.configProductCard?.category}"
              }
            ]
          }
        });
      `;

    const dataLayerScriptForPurchasePage = `
         window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
        event: "purchase",
        ecommerce: {
                    currency: "${seoData?.currency}",
                    transaction_id: "${seoData?.transaction_id}",
                    value: ${seoData?.value},
                    coupon: "${seoData?.coupon}",
                    shipping: 0.00,
                    tax: 0.00,
                    items: ${JSON.stringify(
                        seoData?.items?.map((item) => ({
                            item_id: item?.item_id.toString(),
                            item_name: item?.item_name,
                            item_brand: item?.item_brand,
                            item_category: item?.item_category,
                            price: item?.price,
                            quantity: item?.quantity,
                        }))
                    )}
                }
            });
      `;

    const localBusinessStructuredData = JSON.stringify({
        "@context": seoDefaults.context,
        "@type": "LocalBusiness",
        name: seoData?.centreName,
        image: {
            "@type": "ImageObject",
            url: seoData?.centreBannerImage,
            name: seoData?.centreName,
        },
        "@id": "",
        url: `${NEXT_PUBLIC_DOMAIN_URL}/${seoData?.routerPath}`,
        telephone: seoData?.centreMobile,
        address: {
            "@type": "PostalAddress",
            streetAddress: seoData?.centreAddress,
            addressLocality: seoData?.cityName,
            postalCode: seoData?.pincode,
            addressCountry: "IN",
        },
        geo: {
            "@type": "GeoCoordinates",
            latitude: seoData?.centreLatitude,
            longitude: seoData?.centreLongitude,
        },
    });
    const [showPopup, setShowPopup] = useState(false);
    const notificationData = {
        imageUrl:
            "https://onesignal.com/blog/content/images/2022/02/push-notification-anatomy-2022.png",
        title: "We’re blasting off 🚀",
        message: "Notification message here",
        link: "https://example.com",
    };
    return (
        <>
            <Head>
                <title>{title}</title>

                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <MetaTags
                    title={title}
                    description={description}
                    image={bannerImage}
                    url={pageUrl}
                />

                {defaultStructuredDataPages.includes(
                    seoData?.routerPath?.toLowerCase()
                ) && (
                    <script
                        id="default-structured-data"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: defaultStructuredData,
                        }}
                    />
                )}

                {seoData?.routerPath?.toLowerCase() === "find-a-test" && (
                    <>
                        {pageProps?.query?.page > 1 && (
                            <link
                                rel="prev"
                                href={`/find-a-test?page=${
                                    Number(pageProps?.query?.page || 1) - 1
                                }`}
                            />
                        )}

                        <link
                            rel="next"
                            href={`/find-a-test?page=${
                                Number(pageProps?.query?.page || 1) + 1
                            }`}
                        />
                    </>
                )}
                {/* {seoData?.routerPath?.toLowerCase() === "blog" && (
                    <script
                        id="blog-structured-data"
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: structuredBlogData,
                        }}
                    />
                )} */}
                {seoData?.routerPath?.startsWith("blog/") &&
                    seoData?.routerPath !== "blog" && (
                        <script
                            id="blog-post-structured-data"
                            type="application/ld+json"
                            dangerouslySetInnerHTML={{
                                __html: structuredBlogDetailsData,
                            }}
                        />
                    )}
                {seoData?.routerPath?.startsWith("facilities/") &&
                    seoData?.routerPath.toLowerCase() !== "facilities" && (
                        <script
                            id="facility-detail-structured-data"
                            type="application/ld+json"
                        >
                            {structuredFacilityDetailsData}
                        </script>
                    )}
                {seoData?.routerPath === `test/${seoData?.urlSlug}` && (
                    <>
                        <script
                            id="product-structured-data"
                            type="application/ld+json"
                        >
                            {productStructuredData}
                        </script>
                        <script>{dataLayerScript}</script>
                    </>
                )}
                {seoData?.routerPath ===
                    `locate-us/${seoData?.citySlug}/${seoData?.centreSlug}` && (
                    <script
                        id="local-business-structured-data"
                        type="application/ld+json"
                    >
                        {localBusinessStructuredData}
                    </script>
                )}
                {seoData?.routerPath?.startsWith("careers/") &&
                    seoData?.routerPath.toLowerCase() !== "careers" && (
                        <script
                            id="careers-detail-structured-data"
                            type="application/ld+json"
                        >
                            {structuredCareersDetailsData}
                        </script>
                    )}
                {seoData?.routerPath === "purchase" && (
                    <>
                        <script
                            id="purchase-structured-data"
                            type="text/javascript"
                        >
                            {dataLayerScriptForPurchasePage}
                        </script>
                    </>
                )}
            </Head>

            <Provider>
                {/* <AxiosInterceptor> */}

                <Snackbar />

                <PushNotifications />

                <ErrorBoundary>
                    <Layout>{getLayout(<Component {...pageProps} />)}</Layout>
                </ErrorBoundary>

                {/* </AxiosInterceptor> */}
            </Provider>
        </>
    );
}
